import { SelectChangeEvent, SxProps, Theme } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import {
	InputLabel,
	ListItemText,
	MenuItem,
	Radio,
	Select,
	Typography,
} from 'styles';
import { useTranslation } from 'utils/translations';

import FormControl from 'styles/form/FormControl';
import palette from 'styles/theme/Palette';
import { TRANSLATION_KEYS } from 'utils/constants';

interface SelectGenericProps {
	sx?: SxProps<Theme>;
	name: string;
	items:
		| string[]
		| {
				value: string | undefined;
				text: string;
				id?: number | string;
		  }[];
	action: (payload: string) => void;
	initialValue?: string;
	error?: boolean;
	errorMessage?: string;
	sxForm?: SxProps<Theme>;
	required?: boolean;
	fullWidth?: boolean;
	disabled?: boolean;
}

const SelectGeneric: FC<SelectGenericProps> = ({
	action,
	items,
	name,
	sx,
	initialValue,
	error,
	errorMessage,
	sxForm,
	required,
	fullWidth,
	disabled,
}) => {
	const [selectedValue, setSelectedValue] = useState('');
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});

	const handlerSelectItem = (event: SelectChangeEvent<any | unknown>) => {
		setSelectedValue(event.target.value);
		action(event.target.value);
	};

	useEffect(() => {
		if (initialValue) {
			setSelectedValue(initialValue === 'reset' ? '' : initialValue);
		}
	}, [initialValue]);

	return (
		<FormControl
			sx={{
				...sxForm,
				...(disabled && {
					'& .MuiFilledInput-root.Mui-disabled': {
						backgroundColor: palette.grey6.main,
						transition: 'none',
						border: `1px solid ${palette.grey4.main}`,
					},
					'@media(hover: hover)': {
						'& .MuiFilledInput-root.Mui-disabled:hover': {
							backgroundColor: palette.grey6.main,
							border: `1px solid ${palette.grey4.main}`,
						},
					},
					'& .MuiSelect-select.Mui-disabled': {
						color: palette.primary.main,
						WebkitTextFillColor: palette.primary.main,
					},
				}),
			}}
		>
			<InputLabel
				sx={{
					color: error ? palette.error.main : 'default',
					'&.MuiInputLabel-shrink': {
						color: error ? palette.error.main : palette.grey2.main,
					},
				}}
			>
				{tClientLabels(`commons.selectGenericHeader.${name}`)}
				{required ? ' *' : ''}
			</InputLabel>
			<Select
				sx={{
					...sx,
					height: '56px',
				}}
				multiple={false}
				variant="filled"
				name={tClientLabels(`commons.selectGenericHeader.${name}`) as string}
				value={selectedValue ?? ''}
				native={false}
				displayEmpty={true}
				fullWidth={fullWidth}
				disabled={disabled}
				renderValue={(e: any) => {
					if (typeof items === 'object') {
						const selectedItem = (items as { value: string; text: string }[]).find(
							el => el.value === e,
						);
						if (!selectedItem) return '';
						return selectedItem.text;
					} else {
						return e;
					}
				}}
				MenuProps={{
					anchorOrigin: {
						vertical: 'bottom',
						horizontal: 'left',
					},
					transformOrigin: {
						vertical: 'top',
						horizontal: 'left',
					},
					PaperProps: {
						sx: {
							maxHeight: '268px',
							maxWidth: '400px',
							'& li': {
								padding: '5px 7px',
								whiteSpace: 'normal',
								wordBreak: 'break-word',
								'& .MuiListItemText-root': {
									whiteSpace: 'normal',
								},
								'& .MuiTypography-root': {
									fontSize: '13px',
									fontWeight: 600,
									whiteSpace: 'normal',
									wordBreak: 'break-word',
								},
							},
						},
					},
				}}
				onChange={handlerSelectItem}
				disableUnderline
				error={error}
			>
				{items.map((item, idx) => {
					return typeof item === 'object' ? (
						<MenuItem
							key={`${idx}_${item.value}`}
							value={item.value}
							disableTouchRipple
						>
							<Radio checked={selectedValue === item.value} />
							<ListItemText primary={item.text} />
						</MenuItem>
					) : (
						<MenuItem key={`${idx}_${item}`} value={item} disableTouchRipple>
							<Radio checked={selectedValue === item} />
							<ListItemText primary={item} />
						</MenuItem>
					);
				})}
			</Select>
			{error && (
				<Typography
					variant="bodyLink"
					sx={{ position: 'absolute', bottom: 0 }}
					color={palette.error.main}
				>
					{errorMessage || 'This field is mandatory'}
				</Typography>
			)}
		</FormControl>
	);
};

export default SelectGeneric;
